/**
 * Stytch Email OTP Authentication Hooks (Vanilla JS Version)
 *
 * These hooks use @stytch/vanilla-js for browser-only environments
 * Perfect for Office Add-ins, Chrome Extensions, etc.
 */
import { getStytchVanillaClient } from '../client/vanilla-headless-client';
import { STYTCH_DEFAULT_SESSION_DURATION_MINUTES } from '../../../core/session-durations';
function getStytchErrorMessage(error, fallback) {
    if (!error || typeof error !== 'object')
        return fallback;
    const stytchError = error;
    return stytchError.error_message || stytchError.message || fallback;
}
function getStytchStatusCode(error) {
    if (!error || typeof error !== 'object')
        return undefined;
    const statusCode = error.status_code;
    return typeof statusCode === 'number' ? statusCode : undefined;
}
function getStytchErrorType(error) {
    if (!error || typeof error !== 'object')
        return undefined;
    const errorType = error.error_type;
    return typeof errorType === 'string' ? errorType : undefined;
}
/**
 * Hook to send discovery email OTP
 * Does not require organization - works with discovery flow
 */
export function useSendDiscoveryOTP() {
    const stytch = getStytchVanillaClient();
    return async (email, options) => {
        try {
            if (!stytch) {
                return {
                    success: false,
                    error: 'Stytch client not ready',
                    data: null,
                };
            }
            const params = {
                email_address: email,
                discovery_expiration_minutes: options?.discovery_expiration_minutes || 10,
            };
            // Only include login_template_id if explicitly provided
            if (options?.login_template_id) {
                params.login_template_id = options.login_template_id;
            }
            if (options?.locale) {
                params.locale = options.locale;
            }
            const result = await stytch.otps.email.discovery.send(params);
            return {
                success: true,
                data: result,
            };
        }
        catch (error) {
            return {
                success: false,
                error: getStytchErrorMessage(error, 'Failed to send discovery OTP'),
                data: error,
            };
        }
    };
}
/**
 * Hook to authenticate discovery email OTP
 * Returns intermediate session and discovered organizations
 */
export function useAuthenticateDiscoveryOTP() {
    const stytch = getStytchVanillaClient();
    return async (email, code) => {
        try {
            if (!stytch) {
                return {
                    success: false,
                    error: 'Stytch client not ready',
                    data: null,
                };
            }
            const result = await stytch.otps.email.discovery.authenticate({
                email_address: email,
                code: code,
            });
            return {
                success: true,
                intermediate_session_token: result.intermediate_session_token,
                email_address: result.email_address,
                discovered_organizations: result.discovered_organizations,
                data: result,
            };
        }
        catch (error) {
            return {
                success: false,
                error: getStytchErrorMessage(error, 'Failed to authenticate discovery OTP'),
                data: error,
            };
        }
    };
}
/**
 * Hook to exchange intermediate session for full session
 */
export function useExchangeIntermediateSession() {
    const stytch = getStytchVanillaClient();
    return async (intermediateSessionToken, organizationId, options) => {
        try {
            if (!stytch) {
                return {
                    success: false,
                    error: 'Stytch client not ready',
                    data: null,
                };
            }
            const result = await stytch.discovery.intermediateSessions.exchange({
                organization_id: organizationId,
                session_duration_minutes: options?.session_duration_minutes || STYTCH_DEFAULT_SESSION_DURATION_MINUTES,
            });
            return {
                success: true,
                memberId: result.member?.member_id,
                organizationId: result.organization?.organization_id,
                sessionToken: result.session_token,
                sessionJwt: result.session_jwt,
                memberSession: result.member_session,
                member: result.member,
                organization: result.organization,
                data: result,
            };
        }
        catch (error) {
            return {
                success: false,
                error: getStytchErrorMessage(error, 'Failed to exchange session'),
                data: error,
            };
        }
    };
}
/**
 * Hook to authenticate OAuth discovery
 */
export function useAuthenticateOAuthDiscovery() {
    const stytch = getStytchVanillaClient();
    return async (token) => {
        try {
            if (!stytch) {
                return {
                    success: false,
                    error: 'Stytch client not ready',
                    data: null,
                };
            }
            const result = await stytch.oauth.discovery.authenticate({
                discovery_oauth_token: token,
            });
            return {
                success: true,
                intermediate_session_token: result.intermediate_session_token,
                email_address: result.email_address,
                discovered_organizations: result.discovered_organizations,
                intermediateSessionToken: result.intermediate_session_token,
                discoveredOrganizations: result.discovered_organizations,
                data: result,
            };
        }
        catch (error) {
            return {
                success: false,
                error: getStytchErrorMessage(error, 'Failed to authenticate OAuth'),
                data: error,
            };
        }
    };
}
// ============================================================================
// SSO Hooks (Vanilla JS Version)
// ============================================================================
/**
 * Hook to discover SSO connections for an email address
 * Returns available SSO connections for the email's domain
 */
export function useDiscoverSSOConnections() {
    const stytch = getStytchVanillaClient();
    return async (emailAddress) => {
        try {
            if (!stytch) {
                return {
                    success: false,
                    error: 'Stytch client not ready',
                    data: null,
                };
            }
            const result = await stytch.sso.discoverConnections(emailAddress);
            return {
                success: true,
                statusCode: result.status_code,
                connections: result.connections || [],
                data: result,
            };
        }
        catch (error) {
            return {
                success: false,
                error: getStytchErrorMessage(error, 'Failed to discover SSO connections'),
                data: error,
            };
        }
    };
}
/**
 * Hook to start SSO flow
 * Redirects user to the SSO provider's login page
 */
export function useStartSSOFlow() {
    const stytch = getStytchVanillaClient();
    return async (data) => {
        try {
            if (!stytch) {
                return {
                    success: false,
                    error: 'Stytch client not ready',
                    data: null,
                };
            }
            await stytch.sso.start({
                connection_id: data.connectionId,
                login_redirect_url: data.loginRedirectUrl,
                signup_redirect_url: data.signupRedirectUrl,
            });
            // If we reach here, the redirect was successful
            return {
                success: true,
                data: { redirected: true },
            };
        }
        catch (error) {
            return {
                success: false,
                error: getStytchErrorMessage(error, 'Failed to start SSO flow'),
                data: error,
            };
        }
    };
}
/**
 * Hook to authenticate SSO token
 * Called after redirect back from SSO provider
 */
export function useAuthenticateSSOToken() {
    const stytch = getStytchVanillaClient();
    return async (data) => {
        try {
            if (!stytch) {
                return {
                    success: false,
                    error: 'Stytch client not ready',
                    data: null,
                };
            }
            const result = await stytch.sso.authenticate({
                sso_token: data.ssoToken,
                session_duration_minutes: data.sessionDurationMinutes || STYTCH_DEFAULT_SESSION_DURATION_MINUTES,
                locale: data.locale,
            });
            return {
                success: true,
                statusCode: result.status_code,
                memberId: result.member_id,
                organizationId: result.organization?.organization_id,
                member: result.member,
                organization: result.organization,
                sessionToken: result.session_token,
                sessionJwt: result.session_jwt,
                intermediateSessionToken: result.intermediate_session_token,
                memberAuthenticated: result.member_authenticated,
                mfaRequired: result.mfa_required,
                memberSession: result.member_session,
                data: result,
            };
        }
        catch (error) {
            return {
                success: false,
                error: getStytchErrorMessage(error, 'Failed to authenticate SSO'),
                statusCode: getStytchStatusCode(error),
                errorType: getStytchErrorType(error),
                data: error,
            };
        }
    };
}
